import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, map, mergeMap, Observable, of, race, take, tap, timer } from 'rxjs';
import { AppSelectors } from '../../../store/app.selectors';
import { AppActions } from '../../../store/app.actions';
import { PermissionService } from '../../../permission.service';
import { Permission } from '../../../administration/components/roles/role.model';

function ensurePermissionsLoaded(): Observable<Permission[]> {
  const store = inject(Store);
  const actions$ = inject(Actions);
  const permissionService = inject(PermissionService);

  return store.select(AppSelectors.permissions).pipe(
    take(1),
    mergeMap((permissions) => {
      if (permissions?.length) {
        permissionService.syncPermissions(permissions);
        return of(permissions);
      }

      store.dispatch(AppActions.getPermission());

      return race(
        store.select(AppSelectors.permissions).pipe(
          filter((loadedPermissions): loadedPermissions is Permission[] => !!loadedPermissions?.length),
          take(1)
        ),
        actions$.pipe(
          ofType(AppActions.getPermissionFailure),
          take(1),
          map(() => [] as Permission[])
        ),
        timer(15000).pipe(map(() => [] as Permission[]))
      ).pipe(
        tap((loadedPermissions) => {
          if (loadedPermissions.length) {
            permissionService.syncPermissions(loadedPermissions);
          }
        })
      );
    })
  );
}

export const loadPermissionsGuard: CanActivateFn = () => {
  return ensurePermissionsLoaded().pipe(map(() => true));
};

export const permissionGuard = (permission: string): CanActivateFn => {
  return () => {
    const permissionService = inject(PermissionService);
    const router = inject(Router);

    if (permissionService.hasPermission(permission)) {
      return true;
    }

    return ensurePermissionsLoaded().pipe(
      map(() => {
        if (permissionService.hasPermission(permission)) {
          return true;
        }

        return router.parseUrl('/m/dashboard');
      })
    );
  };
};
